import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Calendar } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import SignInModal from './SignInModal';
import Seo from './Seo';
import { posts, SITE_URL } from '../data/blog';

const Blog = () => {
  const [signInOpen, setSignInOpen] = useState(false);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Steno | PACE MASTER Blog',
    url: `${SITE_URL}/blog`,
    description: 'Advanced stenography speed training guides, court reporter drills, and real-time cadence techniques.',
    blogPost: posts.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      datePublished: p.date,
      url: `${SITE_URL}/blog/${p.slug}`,
      author: { '@type': 'Organization', name: p.author },
    })),
  };

  return (
    <>
      <Seo
        title="Blog — Advanced Stenography Speed Training | Steno PACE MASTER"
        description="Guides on advanced stenography speed training, court reporter speed drills, and real-time cadence monitoring for stenographers in Atlanta, Houston, New York City, Washington DC, and Chicago."
        keywords="advanced stenography speed training, court reporter blog, steno speed drills, cadence monitoring"
        canonical={`${SITE_URL}/blog`}
        jsonLd={jsonLd}
      />
      <div className="pointer-events-none fixed inset-0 hero-radial" />
      <Navbar onSignIn={() => setSignInOpen(true)} />

      <main className="relative z-10 max-w-4xl mx-auto px-6 pt-32 pb-20">
        <div className="text-center mb-14 animate-fade-up">
          <div className="inline-block px-3 py-1 rounded-full bg-[#0e0e15] border border-[#22222b] text-[11px] tracking-[0.2em] text-[#c99a2e] font-semibold mb-5">
            THE PACE MASTER JOURNAL
          </div>
          <h1 className="font-serif-display text-[40px] sm:text-[52px] leading-tight text-white">
            Speed Training <span className="gold-gradient-text">Insights</span>
          </h1>
          <p className="mt-4 text-[15px] text-[#9a9aa4] max-w-2xl mx-auto">
            Practical guides on advanced stenography speed training, plateau-breaking drills, and real-time cadence mastery for working court reporters.
          </p>
        </div>

        <div className="grid gap-6">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="group block card-border rounded-2xl bg-[#0e0e14]/60 p-7 transition-all duration-300 hover:-translate-y-1 hover:bg-[#12121a]/80"
            >
              <div className="flex items-center gap-4 text-[12px] text-[#7c7c86] mb-3">
                <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {new Date(p.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {p.readTime}</span>
              </div>
              <h2 className="font-serif-display text-[24px] text-white group-hover:text-[#f0d27a] transition-colors">{p.title}</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-[#8a8a94]">{p.excerpt}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {p.cities.map((c) => (
                  <span key={c} className="px-2.5 py-1 rounded-md bg-[#141019] border border-[#c99a2e]/20 text-[11px] text-[#c99a2e]">{c}</span>
                ))}
              </div>
              <span className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#e0b84c]">
                Read the guide <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
      </main>

      <Footer />
      <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
    </>
  );
};

export default Blog;
